import { isEmpty, isString } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Banner, Loader, TravelSelectionBlock, TravelSelectionTable, useTravelPurchaseContext } from 'components';
import { useSafeState } from 'hooks';
import { setPassengers, setTravelPurchase } from 'store';

const TravelSelectionStep = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getValues, watch, setValue } = useFormContext();
  const { next, isFormValid, user, travelDeparture, travelDepartureLoading, updateTravelPurchaseLoading } =
    useTravelPurchaseContext();
  /**
   * State for the selected parameters (from, duration, month-year)
   * used to get travel departures informations
   */
  const [travelDepartureParams, setTravelDepartureParams] = useSafeState({});

  /**
   * Watch changes in 'selection.selectedTravel' value
   * And ensure that the button Continue is disabled if no travel is selected
   */
  const selectedTravel = watch('selection.selectedTravel');

  const onNextClick = () => {
    if (!isFormValid()) return;

    const travelSelection = getValues('selection');

    // Save selected passengers categories in step 1, to be used in step 2
    const passengers = travelSelection.passengers.map((passenger) => ({ ...passenger }));
    dispatch(setPassengers(passengers));

    // Create an array to store all participants informations (firstName, name, birthDate, serialNumber...)
    const list = [...passengers].reduce((acc, passengerCategory) => {
      if (passengerCategory.qty === 0) return acc;

      const passengersList = Array.from({ length: passengerCategory.qty }, (_, i) => {
        // Current user must always appear first in the TravelParticipants list
        const isFirstUser = i === 0 && passengerCategory.type === 'adult';
        return {
          firstName: isFirstUser ? user.firstName : '',
          name: isFirstUser ? user.name : '',
          birthDate: isFirstUser ? user.birthDate : null,
          serialNumber: isFirstUser ? user.serialNumber : null,
          passengerCategory
        };
      });

      return acc.concat(passengersList);
    }, []);

    dispatch(setTravelPurchase({ key: 'participants', value: list }));
    dispatch(setTravelPurchase({ key: 'lodging', value: null }));
    setValue('configuration.lodging', null);
    // Save selected travel datas in step 1, to be used in TravelPurchaseSummary
    dispatch(setTravelPurchase({ key: 'departureDate', value: selectedTravel?.departureDate }));
    dispatch(setTravelPurchase({ key: 'returnDate', value: selectedTravel?.returnDate }));
    dispatch(setTravelPurchase({ key: 'duration', value: travelSelection.duration }));
    dispatch(setTravelPurchase({ key: 'departureCity', value: travelSelection.from }));

    // Navigate to next step
    next();
  };

  return (
    <>
      {/* ERROR MESSAGE IF TRAVEL WAS NOT FOUND OR IF PARAMS ARE MISSING IN URL */}
      {!travelDepartureLoading && (isString(travelDeparture) || isEmpty(travelDeparture)) && (
        <div className="min-h-[50vh]">
          <Banner type="error" className="my-6 w-fit">
            {<p>{t('purchase:travelError')}</p>}
          </Banner>
        </div>
      )}

      <TravelSelectionBlock
        travelDepartureParams={travelDepartureParams}
        setTravelDepartureParams={setTravelDepartureParams}
      />
      <TravelSelectionTable
        travelDepartureParams={travelDepartureParams}
        setTravelDepartureParams={setTravelDepartureParams}
      />

      {!travelDepartureLoading && !isEmpty(travelDeparture) && (
        <button
          type="button"
          className="btn purchase-btn"
          disabled={isEmpty(selectedTravel) || updateTravelPurchaseLoading}
          onClick={onNextClick}
        >
          {updateTravelPurchaseLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
          {t('buttons.continue')}
        </button>
      )}
    </>
  );
};

export default TravelSelectionStep;
